import * as actionTypes from "../constants/madeToMeasureConstants";

function createBranch(extra = {}) {
  return {
    loading: false,
    success: false,
    error: null,
    errorCode: "",
    status: 0,
    requestId: "",
    retryAfterSeconds: 0,
    ...extra,
  };
}

const initialState = {
  options: createBranch({
    loaded: false,
    products: [],
    textiles: [],
    haberdashery: [],
  }),
  enquiry: createBranch({
    enquiry: null,
    message: "",
    customerUpdateUrl: "",
    customerAccessToken: "",
  }),
  draft: createBranch({
    draft: null,
  }),
  draftUpdate: createBranch({
    draft: null,
    message: "",
  }),
  draftSubmit: createBranch({
    draft: null,
    enquiry: null,
    message: "",
    customerUpdateUrl: "",
    customerAccessToken: "",
  }),
  draftClear: createBranch({
    draft: null,
    message: "",
  }),
  quote: createBranch({
    quote: null,
    lineItems: [],
    delivery: null,
    estimatedTotal: 0,
    estimatedTotalMinor: 0,
    purchasable: true,
    issues: [],
  }),
  myRequests: createBranch({
    currentRequest: null,
    previousRequests: [],
    requests: [],
  }),
  requestDetails: createBranch({
    request: null,
    enquiry: null,
  }),
  requestPayment: createBranch({
    request: null,
    enquiry: null,
    order: null,
    orderNumber: "",
    payment: null,
    hostedCheckoutUrl: "",
    checkoutUrl: "",
    approvalUrl: "",
    approveUrl: "",
    payerActionUrl: "",
    nextAction: null,
    message: "",
  }),
  paymentStatus: createBranch({
    request: null,
    enquiry: null,
    order: null,
    orderNumber: "",
    payment: null,
    pending: false,
    message: "",
  }),
  customerUpdate: createBranch({
    enquiry: null,
  }),
  customerResponse: createBranch({
    enquiry: null,
    message: "",
  }),
  adminList: createBranch({
    enquiries: [],
  }),
  adminUpdate: createBranch({
    enquiry: null,
    customerUpdateUrl: "",
    customerAccessToken: "",
    message: "",
  }),
  adminQuote: createBranch({
    enquiry: null,
    quoteUrl: "",
    quoteToken: "",
    customerUpdateUrl: "",
    customerAccessToken: "",
    message: "",
  }),
  publicQuote: createBranch({
    quote: null,
  }),
  acceptQuote: createBranch({
    quote: null,
    order: null,
    orderNumber: "",
    payment: null,
    checkoutUrl: "",
    approvalUrl: "",
    approveUrl: "",
    payerActionUrl: "",
    nextAction: null,
    message: "",
  }),
  acceptCustomerUpdate: createBranch({
    enquiry: null,
    order: null,
    orderNumber: "",
    payment: null,
    checkoutUrl: "",
    approvalUrl: "",
    approveUrl: "",
    payerActionUrl: "",
    nextAction: null,
    message: "",
  }),
};

function safeText(value = "") {
  return String(value ?? "").trim();
}

function requestState(branch = {}) {
  return {
    ...branch,
    loading: true,
    success: false,
    error: null,
    errorCode: "",
    status: 0,
    requestId: "",
    retryAfterSeconds: 0,
  };
}

function failState(branch = {}, payload = {}) {
  return {
    ...branch,
    loading: false,
    success: false,
    error: safeText(payload?.message) || "Request failed.",
    errorCode: safeText(payload?.code),
    status: Number(payload?.status || 0),
    requestId: safeText(payload?.requestId),
    retryAfterSeconds: Number(payload?.retryAfterSeconds || 0),
  };
}

function sameRequest(left = {}, right = {}) {
  const leftId = safeText(left?.id || left?._id);
  const rightId = safeText(right?.id || right?._id);

  return Boolean(leftId && rightId && leftId === rightId);
}

function replaceRequestInList(list = [], request = null) {
  if (!request) return list;

  return (Array.isArray(list) ? list : []).map((item) =>
    sameRequest(item, request) ? request : item,
  );
}

function syncKnownRequest(state = {}, request = null) {
  if (!request) return {};

  return {
    myRequests: {
      ...state.myRequests,
      currentRequest: sameRequest(state.myRequests.currentRequest, request)
        ? request
        : state.myRequests.currentRequest,
      previousRequests: replaceRequestInList(
        state.myRequests.previousRequests,
        request,
      ),
      requests: replaceRequestInList(state.myRequests.requests, request),
    },
    requestDetails: {
      ...state.requestDetails,
      request: sameRequest(state.requestDetails.request, request)
        ? request
        : state.requestDetails.request,
      enquiry: sameRequest(state.requestDetails.enquiry, request)
        ? request
        : state.requestDetails.enquiry,
    },
  };
}

export default function madeToMeasureReducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.MADE_TO_MEASURE_OPTIONS_REQUEST:
      return {
        ...state,
        options: requestState(state.options),
      };

    case actionTypes.MADE_TO_MEASURE_OPTIONS_SUCCESS:
      return {
        ...state,
        options: {
          ...state.options,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          loaded: true,
          products: Array.isArray(action.payload?.products)
            ? action.payload.products
            : [],
          textiles: Array.isArray(action.payload?.textiles)
            ? action.payload.textiles
            : [],
          haberdashery: Array.isArray(action.payload?.haberdashery)
            ? action.payload.haberdashery
            : [],
        },
      };

    case actionTypes.MADE_TO_MEASURE_OPTIONS_FAIL:
      return {
        ...state,
        options: failState(state.options, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_OPTIONS_RESET:
      return {
        ...state,
        options: initialState.options,
      };

    case actionTypes.MADE_TO_MEASURE_ENQUIRY_REQUEST:
      return {
        ...state,
        enquiry: requestState(state.enquiry),
      };

    case actionTypes.MADE_TO_MEASURE_ENQUIRY_SUCCESS:
      return {
        ...state,
        enquiry: {
          ...state.enquiry,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          enquiry: action.payload?.enquiry || null,
          message: safeText(action.payload?.message),
          customerUpdateUrl: safeText(action.payload?.customerUpdateUrl),
          customerAccessToken: safeText(action.payload?.customerAccessToken),
        },
      };

    case actionTypes.MADE_TO_MEASURE_ENQUIRY_FAIL:
      return {
        ...state,
        enquiry: failState(state.enquiry, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_ENQUIRY_RESET:
      return {
        ...state,
        enquiry: initialState.enquiry,
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_REQUEST:
      return {
        ...state,
        draft: requestState(state.draft),
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_SUCCESS:
      return {
        ...state,
        draft: {
          ...state.draft,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          draft: action.payload?.draft || null,
        },
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_FAIL:
      return {
        ...state,
        draft: failState(state.draft, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_RESET:
      return {
        ...state,
        draft: initialState.draft,
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_REQUEST:
      return {
        ...state,
        draftUpdate: requestState(state.draftUpdate),
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_SUCCESS:
      return {
        ...state,
        draft: {
          ...state.draft,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          retryAfterSeconds: 0,
          draft: action.payload?.draft || state.draft.draft,
        },
        draftUpdate: {
          ...state.draftUpdate,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          retryAfterSeconds: 0,
          draft: action.payload?.draft || null,
          message: safeText(action.payload?.message),
        },
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_FAIL:
      return {
        ...state,
        draftUpdate: failState(state.draftUpdate, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_RESET:
      return {
        ...state,
        draftUpdate: initialState.draftUpdate,
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_SUBMIT_REQUEST:
      return {
        ...state,
        draftSubmit: requestState(state.draftSubmit),
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_SUBMIT_SUCCESS:
      return {
        ...state,
        draft: {
          ...state.draft,
          draft: action.payload?.draft || state.draft.draft,
        },
        draftSubmit: {
          ...state.draftSubmit,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          retryAfterSeconds: 0,
          draft: action.payload?.draft || null,
          enquiry: action.payload?.enquiry || null,
          message: safeText(action.payload?.message),
          customerUpdateUrl: safeText(action.payload?.customerUpdateUrl),
          customerAccessToken: safeText(action.payload?.customerAccessToken),
        },
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_SUBMIT_FAIL:
      return {
        ...state,
        draftSubmit: failState(state.draftSubmit, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_SUBMIT_RESET:
      return {
        ...state,
        draftSubmit: initialState.draftSubmit,
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_CLEAR_REQUEST:
      return {
        ...state,
        draftClear: requestState(state.draftClear),
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_CLEAR_SUCCESS:
      return {
        ...state,
        draft: {
          ...state.draft,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          retryAfterSeconds: 0,
          draft: action.payload?.draft || state.draft.draft,
        },
        draftUpdate: initialState.draftUpdate,
        draftSubmit: initialState.draftSubmit,
        quote: initialState.quote,
        draftClear: {
          ...state.draftClear,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          retryAfterSeconds: 0,
          draft: action.payload?.draft || null,
          message: safeText(action.payload?.message),
        },
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_CLEAR_FAIL:
      return {
        ...state,
        draftClear: failState(state.draftClear, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_DRAFT_CLEAR_RESET:
      return {
        ...state,
        draftClear: initialState.draftClear,
      };

    case actionTypes.MADE_TO_MEASURE_QUOTE_REQUEST:
      return {
        ...state,
        quote: requestState(state.quote),
      };

    case actionTypes.MADE_TO_MEASURE_QUOTE_SUCCESS: {
      const quote = action.payload?.quote || action.payload || {};

      return {
        ...state,
        quote: {
          ...state.quote,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          retryAfterSeconds: 0,
          quote,
          lineItems: Array.isArray(quote.lineItems) ? quote.lineItems : [],
          delivery: quote.delivery || null,
          estimatedTotal: Number(quote.estimatedTotal || 0),
          estimatedTotalMinor: Number(quote.estimatedTotalMinor || 0),
          purchasable: quote.purchasable !== false,
          issues: Array.isArray(quote.issues) ? quote.issues : [],
        },
      };
    }

    case actionTypes.MADE_TO_MEASURE_QUOTE_FAIL:
      return {
        ...state,
        quote: failState(state.quote, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_QUOTE_RESET:
      return {
        ...state,
        quote: initialState.quote,
      };

    case actionTypes.MADE_TO_MEASURE_MY_REQUESTS_REQUEST:
      return {
        ...state,
        myRequests: requestState(state.myRequests),
      };

    case actionTypes.MADE_TO_MEASURE_MY_REQUESTS_SUCCESS:
      return {
        ...state,
        myRequests: {
          ...state.myRequests,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          currentRequest: action.payload?.currentRequest || null,
          previousRequests: Array.isArray(action.payload?.previousRequests)
            ? action.payload.previousRequests
            : [],
          requests: Array.isArray(action.payload?.requests)
            ? action.payload.requests
            : [],
        },
      };

    case actionTypes.MADE_TO_MEASURE_MY_REQUESTS_FAIL:
      return {
        ...state,
        myRequests: failState(state.myRequests, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_MY_REQUESTS_RESET:
      return {
        ...state,
        myRequests: initialState.myRequests,
      };

    case actionTypes.MADE_TO_MEASURE_REQUEST_DETAILS_REQUEST:
      return {
        ...state,
        requestDetails: requestState(state.requestDetails),
      };

    case actionTypes.MADE_TO_MEASURE_REQUEST_DETAILS_SUCCESS:
      return {
        ...state,
        requestDetails: {
          ...state.requestDetails,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          request: action.payload?.request || action.payload?.enquiry || null,
          enquiry: action.payload?.enquiry || action.payload?.request || null,
        },
      };

    case actionTypes.MADE_TO_MEASURE_REQUEST_DETAILS_FAIL:
      return {
        ...state,
        requestDetails: failState(state.requestDetails, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_REQUEST_DETAILS_RESET:
      return {
        ...state,
        requestDetails: initialState.requestDetails,
      };

    case actionTypes.MADE_TO_MEASURE_REQUEST_PAYMENT_REQUEST:
      return {
        ...state,
        requestPayment: requestState(state.requestPayment),
      };

    case actionTypes.MADE_TO_MEASURE_REQUEST_PAYMENT_SUCCESS: {
      const request = action.payload?.request || action.payload?.enquiry || null;
      const synced = syncKnownRequest(state, request);

      return {
        ...state,
        ...synced,
        requestPayment: {
          ...state.requestPayment,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          request,
          enquiry: action.payload?.enquiry || request,
          order: action.payload?.order || null,
          orderNumber: safeText(action.payload?.orderNumber),
          payment: action.payload?.payment || null,
          hostedCheckoutUrl: safeText(action.payload?.hostedCheckoutUrl),
          checkoutUrl: safeText(action.payload?.checkoutUrl),
          approvalUrl: safeText(action.payload?.approvalUrl),
          approveUrl: safeText(action.payload?.approveUrl),
          payerActionUrl: safeText(action.payload?.payerActionUrl),
          nextAction: action.payload?.nextAction || null,
          message: safeText(action.payload?.message),
        },
      };
    }

    case actionTypes.MADE_TO_MEASURE_REQUEST_PAYMENT_FAIL:
      return {
        ...state,
        requestPayment: failState(state.requestPayment, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_REQUEST_PAYMENT_RESET:
      return {
        ...state,
        requestPayment: initialState.requestPayment,
      };

    case actionTypes.MADE_TO_MEASURE_PAYMENT_STATUS_REQUEST:
      return {
        ...state,
        paymentStatus: requestState(state.paymentStatus),
      };

    case actionTypes.MADE_TO_MEASURE_PAYMENT_STATUS_SUCCESS: {
      const request = action.payload?.request || action.payload?.enquiry || null;
      const synced = syncKnownRequest(state, request);

      return {
        ...state,
        ...synced,
        paymentStatus: {
          ...state.paymentStatus,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          request,
          enquiry: action.payload?.enquiry || request,
          order: action.payload?.order || null,
          orderNumber: safeText(action.payload?.orderNumber),
          payment: action.payload?.payment || null,
          pending: Boolean(action.payload?.pending),
          message: safeText(action.payload?.message),
        },
      };
    }

    case actionTypes.MADE_TO_MEASURE_PAYMENT_STATUS_FAIL:
      return {
        ...state,
        paymentStatus: failState(state.paymentStatus, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_PAYMENT_STATUS_RESET:
      return {
        ...state,
        paymentStatus: initialState.paymentStatus,
      };

    case actionTypes.MADE_TO_MEASURE_CUSTOMER_UPDATE_REQUEST:
      return {
        ...state,
        customerUpdate: requestState(state.customerUpdate),
      };

    case actionTypes.MADE_TO_MEASURE_CUSTOMER_UPDATE_SUCCESS:
      return {
        ...state,
        customerUpdate: {
          ...state.customerUpdate,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          enquiry: action.payload?.enquiry || null,
        },
      };

    case actionTypes.MADE_TO_MEASURE_CUSTOMER_UPDATE_FAIL:
      return {
        ...state,
        customerUpdate: failState(state.customerUpdate, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_CUSTOMER_UPDATE_RESET:
      return {
        ...state,
        customerUpdate: initialState.customerUpdate,
      };

    case actionTypes.MADE_TO_MEASURE_CUSTOMER_RESPONSE_REQUEST:
      return {
        ...state,
        customerResponse: requestState(state.customerResponse),
      };

    case actionTypes.MADE_TO_MEASURE_CUSTOMER_RESPONSE_SUCCESS:
      return {
        ...state,
        customerResponse: {
          ...state.customerResponse,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          enquiry: action.payload?.enquiry || null,
          customerUpdateUrl: safeText(action.payload?.customerUpdateUrl),
          customerAccessToken: safeText(action.payload?.customerAccessToken),
          message: safeText(action.payload?.message),
        },
        customerUpdate: {
          ...state.customerUpdate,
          enquiry: action.payload?.enquiry || state.customerUpdate.enquiry,
        },
      };

    case actionTypes.MADE_TO_MEASURE_CUSTOMER_RESPONSE_FAIL:
      return {
        ...state,
        customerResponse: failState(state.customerResponse, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_CUSTOMER_RESPONSE_RESET:
      return {
        ...state,
        customerResponse: initialState.customerResponse,
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_LIST_REQUEST:
      return {
        ...state,
        adminList: requestState(state.adminList),
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_LIST_SUCCESS:
      return {
        ...state,
        adminList: {
          ...state.adminList,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          enquiries: Array.isArray(action.payload?.enquiries)
            ? action.payload.enquiries
            : [],
        },
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_LIST_FAIL:
      return {
        ...state,
        adminList: failState(state.adminList, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_UPDATE_REQUEST:
      return {
        ...state,
        adminUpdate: requestState(state.adminUpdate),
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_UPDATE_SUCCESS:
      return {
        ...state,
        adminUpdate: {
          ...state.adminUpdate,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          enquiry: action.payload?.enquiry || null,
          message: safeText(action.payload?.message),
        },
        adminList: {
          ...state.adminList,
          enquiries: (state.adminList.enquiries || []).map((item) =>
            item.id === action.payload?.enquiry?.id ||
            item._id === action.payload?.enquiry?._id
              ? action.payload.enquiry
              : item,
          ),
        },
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_UPDATE_FAIL:
      return {
        ...state,
        adminUpdate: failState(state.adminUpdate, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_UPDATE_RESET:
      return {
        ...state,
        adminUpdate: initialState.adminUpdate,
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_QUOTE_REQUEST:
      return {
        ...state,
        adminQuote: requestState(state.adminQuote),
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_QUOTE_SUCCESS:
      return {
        ...state,
        adminQuote: {
          ...state.adminQuote,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          enquiry: action.payload?.enquiry || null,
          quoteUrl: safeText(action.payload?.quoteUrl),
          quoteToken: safeText(action.payload?.quoteToken),
          customerUpdateUrl: safeText(action.payload?.customerUpdateUrl),
          customerAccessToken: safeText(action.payload?.customerAccessToken),
          message: safeText(action.payload?.message),
        },
        adminList: {
          ...state.adminList,
          enquiries: (state.adminList.enquiries || []).map((item) =>
            item.id === action.payload?.enquiry?.id ||
            item._id === action.payload?.enquiry?._id
              ? action.payload.enquiry
              : item,
          ),
        },
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_QUOTE_FAIL:
      return {
        ...state,
        adminQuote: failState(state.adminQuote, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_ADMIN_QUOTE_RESET:
      return {
        ...state,
        adminQuote: initialState.adminQuote,
      };

    case actionTypes.MADE_TO_MEASURE_PUBLIC_QUOTE_REQUEST:
      return {
        ...state,
        publicQuote: requestState(state.publicQuote),
      };

    case actionTypes.MADE_TO_MEASURE_PUBLIC_QUOTE_SUCCESS:
      return {
        ...state,
        publicQuote: {
          ...state.publicQuote,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          quote: action.payload?.quote || null,
        },
      };

    case actionTypes.MADE_TO_MEASURE_PUBLIC_QUOTE_FAIL:
      return {
        ...state,
        publicQuote: failState(state.publicQuote, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_PUBLIC_QUOTE_RESET:
      return {
        ...state,
        publicQuote: initialState.publicQuote,
      };

    case actionTypes.MADE_TO_MEASURE_ACCEPT_QUOTE_REQUEST:
      return {
        ...state,
        acceptQuote: requestState(state.acceptQuote),
      };

    case actionTypes.MADE_TO_MEASURE_ACCEPT_QUOTE_SUCCESS:
      return {
        ...state,
        acceptQuote: {
          ...state.acceptQuote,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          quote: action.payload?.quote || null,
          order: action.payload?.order || null,
          orderNumber: safeText(action.payload?.orderNumber),
          payment: action.payload?.payment || null,
          checkoutUrl: safeText(action.payload?.checkoutUrl),
          approvalUrl: safeText(action.payload?.approvalUrl),
          approveUrl: safeText(action.payload?.approveUrl),
          payerActionUrl: safeText(action.payload?.payerActionUrl),
          nextAction: action.payload?.nextAction || null,
          message: safeText(action.payload?.message),
        },
        publicQuote: {
          ...state.publicQuote,
          quote: action.payload?.quote || state.publicQuote.quote,
        },
      };

    case actionTypes.MADE_TO_MEASURE_ACCEPT_QUOTE_FAIL:
      return {
        ...state,
        acceptQuote: failState(state.acceptQuote, action.payload),
      };

    case actionTypes.MADE_TO_MEASURE_ACCEPT_QUOTE_RESET:
      return {
        ...state,
        acceptQuote: initialState.acceptQuote,
      };

    case actionTypes.MADE_TO_MEASURE_ACCEPT_CUSTOMER_UPDATE_REQUEST:
      return {
        ...state,
        acceptCustomerUpdate: requestState(state.acceptCustomerUpdate),
      };

    case actionTypes.MADE_TO_MEASURE_ACCEPT_CUSTOMER_UPDATE_SUCCESS:
      return {
        ...state,
        acceptCustomerUpdate: {
          ...state.acceptCustomerUpdate,
          loading: false,
          success: true,
          error: null,
          errorCode: "",
          status: 0,
          requestId: "",
          enquiry: action.payload?.enquiry || null,
          order: action.payload?.order || null,
          orderNumber: safeText(action.payload?.orderNumber),
          payment: action.payload?.payment || null,
          checkoutUrl: safeText(action.payload?.checkoutUrl),
          approvalUrl: safeText(action.payload?.approvalUrl),
          approveUrl: safeText(action.payload?.approveUrl),
          payerActionUrl: safeText(action.payload?.payerActionUrl),
          nextAction: action.payload?.nextAction || null,
          message: safeText(action.payload?.message),
        },
        customerUpdate: {
          ...state.customerUpdate,
          enquiry: action.payload?.enquiry || state.customerUpdate.enquiry,
        },
      };

    case actionTypes.MADE_TO_MEASURE_ACCEPT_CUSTOMER_UPDATE_FAIL:
      return {
        ...state,
        acceptCustomerUpdate: failState(
          state.acceptCustomerUpdate,
          action.payload,
        ),
      };

    case actionTypes.MADE_TO_MEASURE_ACCEPT_CUSTOMER_UPDATE_RESET:
      return {
        ...state,
        acceptCustomerUpdate: initialState.acceptCustomerUpdate,
      };

    default:
      return state;
  }
}
